import { api } from '../api/client.ts'

/**
 * Подписка на уведомления.
 *
 * Самое неочевидное здесь — iPhone. Уведомления приходят только если
 * приложение добавлено на экран «Домой», и только с iOS 16.4.
 * В обычной вкладке Safari подписка даже не создастся: браузер просто
 * не покажет запрос разрешения. Поэтому сначала определяем, в каком виде
 * приложение открыто, и говорим человеку правду, а не показываем кнопку,
 * которая ничего не сделает.
 */

export type PushState =
  | { kind: 'unsupported'; reason: string }
  /** iPhone в обычной вкладке: нужно сначала установить на экран «Домой» */
  | { kind: 'needs-install' }
  | { kind: 'not-configured' }
  | { kind: 'denied' }
  | { kind: 'available' }
  | { kind: 'subscribed' }

const isIOS = (): boolean =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPad на новых iOS притворяется настольным Safari
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // Старый признак, который до сих пор единственный работающий на iOS
  (navigator as unknown as { standalone?: boolean }).standalone === true

/**
 * Ключ приходит строкой в особой кодировке, а браузеру нужен массив байтов.
 * Буфер создаётся явно: иначе тип получается слишком широким и не подходит
 * под то, что ожидает браузер.
 */
function decodeKey(key: string): Uint8Array<ArrayBuffer> {
  const padded = (key + '='.repeat((4 - (key.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(padded)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i)
  return bytes
}

let configured: Promise<boolean> | null = null

export async function getPushState(): Promise<PushState> {
  if (!('serviceWorker' in navigator)) {
    return { kind: 'unsupported', reason: 'Браузер не умеет фоновые уведомления' }
  }

  if (!('PushManager' in window)) {
    // Ровно этот случай — iPhone в обычной вкладке Safari
    if (isIOS() && !isStandalone()) return { kind: 'needs-install' }
    return { kind: 'unsupported', reason: 'Браузер не поддерживает уведомления' }
  }

  if (isIOS() && !isStandalone()) return { kind: 'needs-install' }

  // Настроены ли уведомления на сервере — за время работы не меняется,
  // спрашиваем один раз, а не при каждой проверке плашки
  configured ??= api<{ configured: boolean }>('/api/push/key')
    .then((r) => r.configured)
    .catch((error) => {
      configured = null
      throw error
    })
  if (!(await configured)) return { kind: 'not-configured' }

  if (Notification.permission === 'denied') return { kind: 'denied' }

  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  if (existing) return { kind: 'subscribed' }

  return { kind: 'available' }
}

/**
 * Запрашивает разрешение и создаёт подписку.
 * Вызывать только по нажатию человека: браузеры отклоняют запрос
 * разрешения, сделанный сам по себе, при загрузке страницы.
 */
export async function subscribeToPush(): Promise<void> {
  /**
   * Разрешение спрашиваем ПЕРВЫМ делом, до любых запросов к серверу.
   *
   * Safari помнит, что человек нажал кнопку, лишь короткое время. Если
   * сначала сходить на сервер за ключом, нажатие «остывает», и iPhone
   * молча не показывает окно разрешения — кнопка будто не работает.
   */
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Уведомления запрещены. Разрешить их можно только в настройках браузера.'
        : 'Разрешение не получено',
    )
  }

  await createSubscription()
}

/** Завести подписку в браузере и отдать её серверу. Разрешение уже есть. */
async function createSubscription(): Promise<void> {
  const { publicKey } = await api<{ publicKey: string | null }>('/api/push/key')
  if (!publicKey) throw new Error('Уведомления на сервере не настроены')

  const registration = await navigator.serviceWorker.ready
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Без этого браузер не показывает уведомления, полученные в фоне
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey),
    }))

  const raw = subscription.toJSON() as {
    endpoint?: string
    keys?: { p256dh?: string; auth?: string }
  }

  await api('/api/push/subscribe', {
    method: 'POST',
    body: {
      endpoint: raw.endpoint,
      keys: { p256dh: raw.keys?.p256dh, auth: raw.keys?.auth },
      platform: navigator.userAgent.slice(0, 200),
    },
  })
}

/**
 * НАПОМНИТЬ СЕРВЕРУ О СЕБЕ
 *
 * Подписка на уведомления живёт на двух концах: в браузере и в базе
 * сервера. Разойтись они могут молча.
 *
 * Сервер удаляет подписку, если служба доставки несколько раз ответила
 * отказом — например, телефон был долго выключен. Браузер при этом
 * по-прежнему считает себя подписанным, в настройках горит «включено»,
 * и человек уверен, что уведомления придут. Они не придут никогда.
 *
 * Поэтому при каждом запуске приложение молча пересылает свою подписку
 * заново. Сервер принимает её как есть: повторная не плодит записей,
 * а пропавшая — возвращается.
 */
export async function refreshPushSubscription(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      return
    }
    if (Notification.permission !== 'granted') return

    const registration = await navigator.serviceWorker.ready
    const subscription = await registration.pushManager.getSubscription()

    /**
     * Разрешение есть, а подписки нет.
     *
     * Так бывает после нового аккаунта на том же телефоне: старая подписка
     * ушла вместе со старым аккаунтом, а разрешение у сайта осталось.
     * Спрашивать человека второй раз незачем — он уже разрешил.
     */
    if (!subscription) {
      await createSubscription()
      return
    }

    const raw = subscription.toJSON() as {
      endpoint?: string
      keys?: { p256dh?: string; auth?: string }
    }
    if (!raw.endpoint) return

    await api('/api/push/subscribe', {
      method: 'POST',
      body: {
        endpoint: raw.endpoint,
        keys: { p256dh: raw.keys?.p256dh, auth: raw.keys?.auth },
        platform: navigator.userAgent.slice(0, 200),
      },
    })
  } catch {
    // Молча: это починка на всякий случай, а не действие человека
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return

  await api('/api/push/unsubscribe', {
    method: 'POST',
    body: { endpoint: subscription.endpoint },
  }).catch(() => {
    /* на сервере записи могло уже не быть */
  })

  await subscription.unsubscribe()
}

/** Отправить уведомление самому себе: проверка делом, а не на слово. */
export const sendTestPush = (): Promise<{ delivered: number }> =>
  api<{ delivered: number }>('/api/push/test', { method: 'POST' })
